import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { FaHome, FaBuilding, FaDollarSign, FaExchangeAlt, FaCog, FaSignOutAlt, FaWrench, FaAngleDoubleLeft, FaAngleDoubleRight, FaUniversity, FaCreditCard } from 'react-icons/fa';
import logo from '../Assets/RSIDE.png';
import './Sidebar.css';

function Sidebar({ initials }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 1280);
  const originalUid = useRef(auth.currentUser?.uid || null);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(user => {
      if (!user) { navigate('/login'); return; }
      if (originalUid.current && user.uid !== originalUid.current) {
        navigate('/login');
      }
    });
    return unsub;
  }, [navigate]);

  useEffect(() => {
    document.body.classList.toggle('sidebar-collapsed', collapsed);
    return () => document.body.classList.remove('sidebar-collapsed');
  }, [collapsed]);

  const isActive = (path) => {
    if (path === '/home') {
      return location.pathname === '/home' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const closeMobile = () => document.body.classList.remove('sidebar-mobile-open');

  const navTo = (path) => { navigate(path); closeMobile(); };

  const handleLogout = async () => {
    closeMobile();
    localStorage.removeItem('bc_gate_cleared');
    await signOut(auth);
    navigate('/login');
  };

  return (
    <>
    <div
      className="sidebar-mobile-backdrop"
      onClick={() => document.body.classList.remove('sidebar-mobile-open')}
    />
    <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>

      {/* Toggle tab — sits on the right border line */}
      <button className="sidebar-toggle" onClick={() => setCollapsed(c => !c)} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
        {collapsed ? <FaAngleDoubleRight /> : <FaAngleDoubleLeft />}
      </button>

      <div className="sidebar-header">
        <div className="sidebar-logo-wrap">
          <img src={logo} alt="RSIDE Logo" className="sidebar-logo" />
        </div>
      </div>

      <div className="sidebar-nav">
        <div className={`nav-item ${isActive('/home') ? 'active' : ''}`} onClick={() => navTo('/home')} title="Dashboard">
          <FaHome className="nav-icon" />
          <span className="nav-label">Dashboard</span>
        </div>

        <div className={`nav-item ${isActive('/properties') ? 'active' : ''}`} onClick={() => navTo('/properties')} title="Properties">
          <FaBuilding className="nav-icon" />
          <span className="nav-label">Properties</span>
        </div>

        <div className={`nav-item ${isActive('/activity') ? 'active' : ''}`} onClick={() => navTo('/activity')} title="Activity">
          <FaDollarSign className="nav-icon" />
          <span className="nav-label">Activity</span>
        </div>

        <div className={`nav-item ${isActive('/work-orders') ? 'active' : ''}`} onClick={() => navTo('/work-orders')} title="Work Orders">
          <FaWrench className="nav-icon" />
          <span className="nav-label">Work Orders</span>
        </div>

        <div className={`nav-item ${isActive('/compare') ? 'active' : ''}`} onClick={() => navTo('/compare')} title="Compare">
          <FaExchangeAlt className="nav-icon" />
          <span className="nav-label">Compare</span>
        </div>

        <div className={`nav-item ${isActive('/bank-connection') ? 'active' : ''}`} onClick={() => navTo('/bank-connection')} title="Bank Connection">
          <FaUniversity className="nav-icon" />
          <span className="nav-label">Bank Connection</span>
        </div>

        <div className={`nav-item ${isActive('/stripe') ? 'active' : ''}`} onClick={() => navTo('/stripe')} title="Stripe Payments">
          <FaCreditCard className="nav-icon" />
          <span className="nav-label">Payments</span>
        </div>
      </div>

      <div className="sidebar-footer">
        <div className={`nav-item ${isActive('/settings') ? 'active' : ''}`} onClick={() => navTo('/settings')} title="Settings">
          <FaCog className="nav-icon" />
          <span className="nav-label">Settings</span>
        </div>

        <div className="nav-item" onClick={handleLogout} title="Logout">
          <FaSignOutAlt className="nav-icon" />
          <span className="nav-label">Logout</span>
        </div>
      </div>
    </div>
    </>
  );
}

export default Sidebar;
